/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { toast } from 'react-toastify'
import { PdfFormPanel } from 'components/molecules/PdfFormPanel'
import {
  DEVICE_COMPLAINT_AUTO_FILLED_FIELD_NAMES,
  DEVICE_COMPLAINT_CATEGORIES,
  DEVICE_COMPLAINT_DESCRIPTION_MAX_LENGTH,
  DeviceComplaintSite,
} from 'constants/deviceComplaints'
import { ORDER_INFORMATION_FIELD_KEYS } from 'constants/pdfFormFields'
import { getFilledPdfFields, getTemplatePdfUrl } from 'helpers/pdfFormFields'
import { useFileDeviceComplaintMutation, useGetDeviceComplaintTemplateQuery } from 'store/services/deviceComplaints'
import { FileDeviceComplaintDto } from 'store/types'

const ModalContent = styled.div`
  display: flex;
  height: calc(90vh - 155px);
  gap: 1rem;
`

const LeftPanel = styled.div`
  width: 320px;
  border-right: 1px solid #e5e7eb;
  padding-right: 1rem;
  overflow-y: auto;
`

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

interface DeviceComplaintModalProps {
  open: boolean
  onClose: () => void
  site: DeviceComplaintSite
  deviceId: number
  orderId?: number
  deviceLabel?: string
}

const INITIAL_DETAILS = {
  category: '',
  description: '',
}

export const DeviceComplaintModal = ({ open, onClose, site, deviceId, orderId, deviceLabel }: DeviceComplaintModalProps) => {
  const [details, setDetails] = useState(INITIAL_DETAILS)
  const [formFields, setFormFields] = useState<Record<string, string>>({})
  const { data: template, isLoading: isLoadingTemplate, isError: isTemplateError } =
    useGetDeviceComplaintTemplateQuery(site, { skip: !open })
  const [fileComplaint, { isLoading: isSubmitting }] = useFileDeviceComplaintMutation()

  useEffect(() => {
    if (!open) {
      setDetails(INITIAL_DETAILS)
      setFormFields({})
    }
  }, [open])

  const canSubmit = !!details.category && !!details.description.trim() && !!template && !isSubmitting

  const handleSubmit = async () => {
    try {
      await fileComplaint({
        site,
        deviceId,
        orderId,
        category: details.category as FileDeviceComplaintDto['category'],
        description: details.description.trim(),
        formFields: getFilledPdfFields(formFields),
      }).unwrap()
      toast.success('Complaint reported.')
      onClose()
    } catch (err: any) {
      toast.error(err?.data?.message || 'Could not report the complaint.')
    }
  }

  return (
    <Modal show={open} onHide={onClose} size="xl" centered dialogClassName="modal-80h">
      <Modal.Header>
        <Modal.Title>Report Device Complaint</Modal.Title>
      </Modal.Header>

      <Modal.Body css={{ padding: '1rem' }}>
        <ModalContent>
          <LeftPanel>
            <div className="text-muted mb-3" css={{ fontSize: '14px' }}>
              Device: {deviceLabel || `#${deviceId}`}
              {orderId && <div>Order: #{orderId}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="complaint-category">Category *</label>
              <select
                id="complaint-category"
                className="form-control"
                value={details.category}
                onChange={(e) => setDetails({ ...details, category: e.target.value })}>
                <option value="">Select</option>
                {DEVICE_COMPLAINT_CATEGORIES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="complaint-description">Description *</label>
              <textarea
                id="complaint-description"
                className="form-control"
                rows={8}
                maxLength={DEVICE_COMPLAINT_DESCRIPTION_MAX_LENGTH}
                value={details.description}
                onChange={(e) => setDetails({ ...details, description: e.target.value })}
              />
            </div>
          </LeftPanel>

          <RightPanel>
            {template ? (
              <PdfFormPanel
                pdfUrl={getTemplatePdfUrl(template)}
                title={template.fileName.replace(/\.pdf$/i, '')}
                autoFilledKeys={ORDER_INFORMATION_FIELD_KEYS}
                autoFilledFieldNames={DEVICE_COMPLAINT_AUTO_FILLED_FIELD_NAMES}
                showCheckboxes
                values={formFields}
                onChange={(name, value) => setFormFields((prev) => ({ ...prev, [name]: value }))}
                fieldsTabLabel="Complaint Form Fields"
                fieldsHint="These are empty fields of the complaint form that are not filled from the device, order and complaint details."
              />
            ) : (
              <div css={{ margin: 'auto', color: isTemplateError ? '#ef4444' : '#6b7280' }}>
                {isLoadingTemplate ? 'Loading complaint form...' : 'The complaint form template is not available.'}
              </div>
            )}
          </RightPanel>
        </ModalContent>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex-grow-1 text-muted" css={{ fontSize: '14px' }}>
          <i className="fas fa-info-circle mr-2" />
          The filled complaint form is generated and attached to the complaint record
        </div>
        <Button variant="outline-secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2" />
              Submitting...
            </>
          ) : (
            'Submit Complaint'
          )}
        </Button>
      </Modal.Footer>

      <style>{`
        .modal-80h .modal-content {
          height: 90vh;
          display: flex;
          flex-direction: column;
        }
        .modal-80h .modal-body {
          flex: 1;
          overflow: hidden;
        }
      `}</style>
    </Modal>
  )
}
